/**
 * Panel de trazabilidad de fibra end-to-end
 * @module components/panels/FiberTracePanel
 */

import { useState, useMemo, useEffect } from 'react'
import { X, Search, Download, AlertCircle, CheckCircle, Activity, Zap } from 'lucide-react'
import { useNodeStore, useFiberStore, useIlluminationStore } from '@/stores'
import type { PathSegment } from '@/types/fibers'
import { isCliente } from '@/types/nodes'
import { exportTraceToPDF } from '@/utils/pdfExport'
import {
  calcularDesdeRuta,
  computeNapSplitterTotals,
  computeNapSplitterLossAlongCables,
  longitudRealCable,
  type TracePathSegment,
  type IntegratedBudgetResult,
} from '@/services/opticalBudgetService'
import { cableLabelShort } from '@/utils/cableDisplayLabel'
import { describeFiberIdForUi } from '@/utils/fiberIdDisplay'
import type { Cable } from '@/types/fibers'

interface FiberTracePanelProps {
  isOpen: boolean
  onClose: () => void
}

const OPTICAL_LS_KEY = 'neteasyftx-trace-optical-v1'

type OpticalParams = { potenciaTx: number; sensibilidadRx: number; margenDiseno: number }

function readStoredOpticalParams(): OpticalParams {
  if (typeof window === 'undefined') {
    return { potenciaTx: 3, sensibilidadRx: -28, margenDiseno: 3 }
  }
  try {
    const raw = localStorage.getItem(OPTICAL_LS_KEY)
    if (raw) {
      const p = JSON.parse(raw) as Partial<OpticalParams>
      return {
        potenciaTx: typeof p.potenciaTx === 'number' ? p.potenciaTx : 3,
        sensibilidadRx: typeof p.sensibilidadRx === 'number' ? p.sensibilidadRx : -28,
        margenDiseno: typeof p.margenDiseno === 'number' ? p.margenDiseno : 3,
      }
    }
  } catch {
    /* ignore */
  }
  return { potenciaTx: 3, sensibilidadRx: -28, margenDiseno: 3 }
}

const SFP_PRESETS: { id: string; label: string; potenciaTx: number }[] = [
  { id: 'c_plus', label: 'SFP C+ (≈3 dBm)', potenciaTx: 3 },
  { id: 'c_plus_plus', label: 'SFP C++ (≈5 dBm)', potenciaTx: 5 },
  { id: 'c_plus_plus_plus', label: 'SFP C+++ / alto (≈7 dBm)', potenciaTx: 7 },
]

export function FiberTracePanel({ isOpen, onClose }: FiberTracePanelProps) {
  const nodes = useNodeStore((state) => state.nodes)
  const cables = useFiberStore((state) => state.cables)
  const activeTrace = useFiberStore((state) => state.activeTrace)
  const traceFiber = useFiberStore((state) => state.traceFiber)
  const traceFromClient = useFiberStore((state) => state.traceFromClient)
  const clearTrace = useFiberStore((state) => state.clearTrace)
  const setIlluminatedPath = useIlluminationStore((state) => state.setIlluminatedPath)
  const clearIllumination = useIlluminationStore((state) => state.clearIllumination)

  const [traceMode, setTraceMode] = useState<'fiber' | 'client'>('client')
  const [selectedClientId, setSelectedClientId] = useState('')
  const [selectedFiberId, setSelectedFiberId] = useState('')
  const [isTracing, setIsTracing] = useState(false)
  const [opticalParams, setOpticalParams] = useState<OpticalParams>(readStoredOpticalParams)
  const [sfpPresetId, setSfpPresetId] = useState<string>('custom')

  useEffect(() => {
    try {
      localStorage.setItem(OPTICAL_LS_KEY, JSON.stringify(opticalParams))
    } catch {
      /* ignore */
    }
  }, [opticalParams])

  // Calcular presupuesto óptico integrado cuando hay trace activo
  const integratedBudget = useMemo((): IntegratedBudgetResult | null => {
    if (!activeTrace || !activeTrace.success || !activeTrace.path) return null

    try {
      const pathSegments: TracePathSegment[] = []
      const traceWarnings: string[] = []
      const nodeMap = new Map(nodes.map(n => [n.id, n]))
      const cableMap = new Map(cables.map(c => [c.id, c]))
      const pathArr = activeTrace.path

      for (let i = 0; i < pathArr.length; i++) {
        const segment = pathArr[i]
        // Agregar nodo
        if (segment.nodeId) {
          const node = nodeMap.get(segment.nodeId)
          if (node) {
            const ref = (node as any).refCode || segment.nodeRefCode
            const nom = (node as any).nombre || segment.description
            const name = [ref, nom].filter(Boolean).join(' · ') || segment.nodeId
            const splitters = (node as any).config?.splitters
            const puertos = (node as any).config?.puertos
            let napSpl = { lossDb: 0, count: 0 }

            if (node.tipo === 'NAP') {
              const ingress = (segment.cableId || '').trim()
              const egress = (pathArr[i + 1]?.cableId || '').trim()
              if (ingress && egress) {
                const pathRes = computeNapSplitterLossAlongCables(
                  splitters,
                  ingress,
                  egress,
                  puertos
                )
                if (pathRes.unmatched) {
                  traceWarnings.push(
                    `${name}: la traza entra y sale por cables distintos pero la config de splitters no enlaza esa ruta; no se sumó pérdida de splitter aquí (revisa entradas/salidas en la NAP).`
                  )
                  napSpl = { lossDb: 0, count: 0 }
                } else {
                  napSpl = { lossDb: pathRes.lossDb, count: pathRes.count }
                }
              } else {
                napSpl = computeNapSplitterTotals(splitters)
                if (!ingress || !egress) {
                  traceWarnings.push(
                    `${name}: falta cable de entrada o salida en la traza; se usan todos los splitters configurados en la NAP.`
                  )
                }
              }
            }

            const firstRatio =
              Array.isArray(splitters) && splitters[0]?.ratio ? splitters[0].ratio : undefined
            pathSegments.push({
              type: 'node',
              id: segment.nodeId,
              name,
              nodeType: node.tipo,
              napSplitterLossDb: napSpl.lossDb > 0 ? napSpl.lossDb : undefined,
              napSplitterCount: napSpl.count > 0 ? napSpl.count : undefined,
              splitterRatio:
                napSpl.lossDb <= 0 && firstRatio ? String(firstRatio) : undefined,
            })
          }
        }
        // Agregar cable
        if (segment.cableId) {
          const cable = cableMap.get(segment.cableId)
          if (cable) {
            pathSegments.push({
              type: 'cable',
              id: segment.cableId,
              name: cableLabelShort(cable as Cable),
              cableType: cable.tipo,
              // La longitud real del cable instalado (con gazas/reservas) manda sobre la
              // distancia recta nodo-a-nodo que calcula el backend -- es la que de verdad
              // importa para atenuación por distancia.
              longitudM: longitudRealCable(cable) || segment.distance || 0,
              fibras: cable.numeroFibras,
            })
          }
        }
      }

      if (pathSegments.length === 0) return null

      const base = calcularDesdeRuta({
        path: pathSegments,
        potenciaTx: opticalParams.potenciaTx,
        sensibilidadRx: opticalParams.sensibilidadRx,
        margenDiseno: opticalParams.margenDiseno,
      })
      return {
        ...base,
        advertencias: [...base.advertencias, ...traceWarnings],
      }
    } catch (err) {
      console.error('Error calculando presupuesto integrado:', err)
      return null
    }
  }, [activeTrace, nodes, cables, opticalParams])

  if (!isOpen) return null

  const clientNodes = nodes.filter(isCliente)

  const handleTrace = () => {
    setIsTracing(true)
    clearTrace()
    clearIllumination()

    try {
      if (traceMode === 'client') {
        if (!selectedClientId) {
          alert('Debe seleccionar un cliente')
          setIsTracing(false)
          return
        }
        traceFromClient(selectedClientId)
      } else {
        if (!selectedFiberId) {
          alert('Debe ingresar un ID de fibra')
          setIsTracing(false)
          return
        }
        traceFiber(selectedFiberId)
      }
    } catch (error) {
      console.error('Error al trazar:', error)
      alert('Error al trazar la fibra')
    } finally {
      setIsTracing(false)
    }
  }

  // Iluminar la ruta en el mapa
  const handleIlluminateTrace = () => {
    if (!activeTrace || !activeTrace.path) return

    const cableIds: string[] = []
    const nodeIds: string[] = []

    for (const segment of activeTrace.path) {
      if (segment.cableId && !cableIds.includes(segment.cableId)) {
        cableIds.push(segment.cableId)
      }
      if (segment.nodeId && !nodeIds.includes(segment.nodeId)) {
        nodeIds.push(segment.nodeId)
      }
    }

    setIlluminatedPath({
      cables: cableIds,
      fibras: activeTrace.fiberId ? [activeTrace.fiberId] : [],
      nodes: nodeIds,
      potenciaInicial: integratedBudget?.potenciaTx,
      potenciaFinal: integratedBudget ? integratedBudget.potenciaTx - integratedBudget.perdidaTotal : undefined,
      perdidaTotal: integratedBudget?.perdidaTotal,
    })
  }

  const handleExportPDF = () => {
    if (!activeTrace || !activeTrace.success) {
      alert('No hay trazabilidad activa para exportar')
      return
    }

    try {
      // Crear mapa de nodos para búsqueda rápida
      const nodeMap = new Map(nodes.map((node) => [node.id, node]))

      // Obtener el nodo cliente si se trazó desde cliente
      const clientNode = traceMode === 'client' && selectedClientId
        ? nodeMap.get(selectedClientId)
        : undefined

      // Exportar a PDF
      const cableMap = new Map(cables.map((c) => [c.id, c]))
      const fileName = exportTraceToPDF({
        trace: activeTrace,
        clientNode,
        nodeMap,
        cableMap,
        integratedBudget,
      })

      alert(`✅ Reporte generado exitosamente: ${fileName}`)
    } catch (error) {
      console.error('Error al exportar PDF:', error)
      alert('Error al generar el reporte PDF')
    }
  }

  const linkStatusVisual = (b: IntegratedBudgetResult | null) => {
    if (!b) {
      return {
        colorClass: 'text-gray-600 dark:text-gray-400',
        icon: <Activity className="w-5 h-5" />,
        title: 'Sin presupuesto detallado',
        subtitle: 'Carga nodos y cables para ver splitters y pérdidas completas.',
      }
    }
    if (b.esViable && b.margenRestante >= 3) {
      return {
        colorClass: 'text-green-600 dark:text-green-400',
        icon: <CheckCircle className="w-5 h-5" />,
        title: 'Enlace viable',
        subtitle: `Margen ${b.margenRestante.toFixed(1)} dB`,
      }
    }
    if (b.esViable) {
      return {
        colorClass: 'text-yellow-600 dark:text-yellow-400',
        icon: <AlertCircle className="w-5 h-5" />,
        title: 'Enlace viable (margen bajo)',
        subtitle: `Margen ${b.margenRestante.toFixed(1)} dB`,
      }
    }
    return {
      colorClass: 'text-red-600 dark:text-red-400',
      icon: <AlertCircle className="w-5 h-5" />,
      title: 'Enlace no viable',
      subtitle: `Margen ${b.margenRestante.toFixed(1)} dB`,
    }
  }

  const statusUi = linkStatusVisual(integratedBudget)

  return (
    <aside className="w-96 bg-white dark:bg-dark-800 border-l border-gray-200 dark:border-dark-700 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-dark-700">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold">Trazabilidad</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-dark-700 rounded"
            title="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 leading-snug">
          Sigue la fibra desde el cliente o desde un ID de fibra hasta el POP/OLT. Verás la ruta, pérdidas
          aproximadas y podrás iluminar el trayecto en el mapa.
        </p>

        {/* Mode selector */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => setTraceMode('client')}
            className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${
              traceMode === 'client'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600'
            }`}
          >
            Por Cliente
          </button>
          <button
            onClick={() => setTraceMode('fiber')}
            className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${
              traceMode === 'fiber'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600'
            }`}
          >
            Por Fibra
          </button>
        </div>

        {/* Input selector */}
        <div className="space-y-2">
          {traceMode === 'client' ? (
            <div>
              <label className="block text-sm font-medium mb-1">
                Seleccionar Cliente
              </label>
              <select
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-dark-700 text-sm"
              >
                <option value="">Seleccionar...</option>
                {clientNodes.map((client) => {
                  const ref = (client as any).refCode
                  const nom = (client as any).nombre || 'Cliente'
                  const extra =
                    client.servicio?.tipoServicio || client.servicio?.plan || ''
                  const label = [ref, nom, extra].filter(Boolean).join(' · ')
                  return (
                    <option key={client.id} value={client.id}>
                      {label}
                    </option>
                  )
                })}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium mb-1">
                ID de Fibra
              </label>
              <input
                type="text"
                value={selectedFiberId}
                onChange={(e) => setSelectedFiberId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-dark-700 text-sm"
                placeholder="Ej: CAB-01|azul|rojo"
              />
              <p className="text-xs text-gray-500 mt-1">
                Formato: CABLE-ID|buffer|color
              </p>
            </div>
          )}

          <button
            onClick={handleTrace}
            disabled={isTracing}
            className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Search className="w-4 h-4" />
            {isTracing ? 'Trazando...' : 'Trazar Fibra'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {!activeTrace ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 dark:text-gray-400">
            <Activity className="w-16 h-16 mb-3 opacity-50" />
            <p className="text-sm">No hay trazabilidad activa</p>
            <p className="text-xs mt-1">
              Selecciona un cliente o fibra y presiona "Trazar Fibra"
            </p>
          </div>
        ) : activeTrace.success ? (
          <div className="space-y-4">
            {/* Estado coherente con presupuesto óptico (incl. splitters) */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className={statusUi.colorClass}>{statusUi.icon}</div>
                <div>
                  <h3 className="font-semibold text-sm">Estado del enlace</h3>
                  <p className={`text-xs font-medium ${statusUi.colorClass}`}>{statusUi.title}</p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">{statusUi.subtitle}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-white/50 dark:bg-dark-800/50 rounded p-2">
                  <p className="text-gray-600 dark:text-gray-400">Pérdida total</p>
                  <p className={`font-bold ${statusUi.colorClass}`}>
                    {integratedBudget ? `${integratedBudget.perdidaTotal.toFixed(2)} dB` : '—'}
                  </p>
                </div>
                <div className="bg-white/50 dark:bg-dark-800/50 rounded p-2">
                  <p className="text-gray-600 dark:text-gray-400">Distancia (traza API)</p>
                  <p className="font-bold">{activeTrace.totalDistanceKm.toFixed(2)} km</p>
                </div>
                {integratedBudget && (
                  <>
                    <div className="bg-white/50 dark:bg-dark-800/50 rounded p-2 col-span-2">
                      <p className="text-gray-600 dark:text-gray-400">Potencia estimada en ONT</p>
                      <p className="font-bold text-indigo-700 dark:text-indigo-300">
                        {(integratedBudget.potenciaTx - integratedBudget.perdidaTotal).toFixed(2)} dBm
                        <span className="text-[10px] font-normal text-gray-500 ml-1">
                          (Tx {integratedBudget.potenciaTx} − pérdidas)
                        </span>
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Fiber Info */}
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                Información de Fibra
              </h4>
              <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3 space-y-2 text-xs">
                <div>
                  <span className="text-gray-600 dark:text-gray-400 block mb-0.5">Fibra / enlace</span>
                  <span className="font-medium text-sm leading-snug">
                    {describeFiberIdForUi(activeTrace.fiberId, cables).primary}
                  </span>
                </div>
                <details className="group">
                  <summary className="cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                    ID técnico (copiar)
                  </summary>
                  <p className="mt-1 font-mono text-[10px] break-all text-gray-600 dark:text-gray-400 select-all">
                    {activeTrace.fiberId}
                  </p>
                </details>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Segmentos:</span>
                  <span className="font-medium">{activeTrace.path.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Empalmes:</span>
                  <span className="font-medium">{activeTrace.splicingPoints.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Conectores:</span>
                  <span className="font-medium">{activeTrace.connectorsCount}</span>
                </div>
              </div>
            </div>

            {/* Path */}
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                Ruta Completa
              </h4>
              <div className="space-y-2">
                {activeTrace.path.map((segment, index) => (
                  <PathSegmentItem
                    key={`${segment.nodeId}-${index}`}
                    segment={segment}
                    index={index}
                    isLast={index === activeTrace.path.length - 1}
                  />
                ))}
              </div>
            </div>

            {/* Real-connection warnings */}
            {activeTrace.warnings.length > 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-3">
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2 text-amber-800 dark:text-amber-300">
                  <span>⚠️</span>
                  Ruta no verificada ({activeTrace.warnings.length})
                </h4>
                <p className="text-xs text-amber-700 dark:text-amber-400 mb-2">
                  Esta ruta existe topológicamente pero no hay empalme real registrado en los siguientes puntos — la fibra podría no estar realmente conectada:
                </p>
                <ul className="space-y-1 text-xs text-amber-800 dark:text-amber-300 list-disc list-inside">
                  {activeTrace.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Splicing Points */}
            {activeTrace.splicingPoints.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
                  Puntos de Empalme
                </h4>
                <div className="space-y-1">
                  {activeTrace.splicingPoints.map((point, index) => (
                    <SplicePointRow key={`${point}-${index}`} nodeId={point} />
                  ))}
                </div>
              </div>
            )}

            {/* Parámetros Tx/Rx (SFP / ONT) — persistidos en el navegador */}
            <details className="group rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/80 dark:bg-amber-950/20">
              <summary className="cursor-pointer list-none flex items-center justify-between px-3 py-2 text-sm font-medium text-amber-900 dark:text-amber-200">
                <span className="flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Potencia SFP / sensibilidad ONT
                </span>
                <span className="text-xs text-amber-700 dark:text-amber-300 group-open:hidden">Editar</span>
              </summary>
              <div className="px-3 pb-3 pt-0 space-y-2 text-xs">
                <p className="text-[11px] text-amber-800/90 dark:text-amber-200/80">
                  Ajusta según clase de SFP (C+, C++, C+++) y especificación del receptor. Se guarda en este navegador.
                </p>
                <label className="block text-gray-600 dark:text-gray-400">Preset rápido (solo Tx)</label>
                <select
                  value={sfpPresetId}
                  onChange={(e) => {
                    const id = e.target.value
                    setSfpPresetId(id)
                    const pr = SFP_PRESETS.find((p) => p.id === id)
                    if (pr) {
                      setOpticalParams((o) => ({ ...o, potenciaTx: pr.potenciaTx }))
                    }
                  }}
                  className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-dark-600 dark:bg-dark-700"
                >
                  <option value="custom">Personalizado (usar campos abajo)</option>
                  {SFP_PRESETS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <div className="grid grid-cols-3 gap-2">
                  <label className="flex flex-col gap-0.5">
                    <span className="text-gray-500">Tx (dBm)</span>
                    <input
                      type="number"
                      step="0.1"
                      value={opticalParams.potenciaTx}
                      onChange={(e) => {
                        setSfpPresetId('custom')
                        setOpticalParams((o) => ({ ...o, potenciaTx: Number(e.target.value) }))
                      }}
                      className="px-2 py-1 rounded border dark:bg-dark-700 dark:border-dark-600"
                    />
                  </label>
                  <label className="flex flex-col gap-0.5">
                    <span className="text-gray-500">Sens. Rx (dBm)</span>
                    <input
                      type="number"
                      step="0.1"
                      value={opticalParams.sensibilidadRx}
                      onChange={(e) =>
                        setOpticalParams((o) => ({ ...o, sensibilidadRx: Number(e.target.value) }))
                      }
                      className="px-2 py-1 rounded border dark:bg-dark-700 dark:border-dark-600"
                    />
                  </label>
                  <label className="flex flex-col gap-0.5">
                    <span className="text-gray-500">Margen diseño (dB)</span>
                    <input
                      type="number"
                      step="0.1"
                      min={0}
                      value={opticalParams.margenDiseno}
                      onChange={(e) =>
                        setOpticalParams((o) => ({ ...o, margenDiseno: Number(e.target.value) }))
                      }
                      className="px-2 py-1 rounded border dark:bg-dark-700 dark:border-dark-600"
                    />
                  </label>
                </div>
              </div>
            </details>

            {/* Presupuesto Óptico Integrado */}
            {integratedBudget && (
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-500" />
                  Presupuesto óptico (desglose)
                </h4>
                <div className={`rounded-lg p-3 border ${
                  integratedBudget.esViable 
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium">
                      {integratedBudget.esViable ? '✓ Enlace viable' : '✗ Enlace no viable'}
                    </span>
                    <span className={`text-sm font-bold ${
                      integratedBudget.esViable ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {integratedBudget.margenRestante.toFixed(1)} dB margen
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-white/50 dark:bg-dark-800/50 rounded p-2">
                      <p className="text-gray-500">Tx</p>
                      <p className="font-bold">{integratedBudget.potenciaTx} dBm</p>
                    </div>
                    <div className="bg-white/50 dark:bg-dark-800/50 rounded p-2">
                      <p className="text-gray-500">Rx (sens.)</p>
                      <p className="font-bold">{integratedBudget.sensibilidadRx} dBm</p>
                    </div>
                  </div>

                  <div className="mt-2 space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Fibra ({integratedBudget.longitudTotalKm.toFixed(2)} km):</span>
                      <span className="text-red-600">-{integratedBudget.perdidaFibra.toFixed(2)} dB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Empalmes ({integratedBudget.totalEmpalmes}):</span>
                      <span className="text-red-600">-{integratedBudget.perdidaEmpalmes.toFixed(2)} dB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Conectores ({integratedBudget.totalConectores}):</span>
                      <span className="text-red-600">-{integratedBudget.perdidaConectores.toFixed(2)} dB</span>
                    </div>
                    {integratedBudget.perdidaSplitter > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Splitters ({integratedBudget.totalSplitters}):</span>
                        <span className="text-red-600">-{integratedBudget.perdidaSplitter.toFixed(2)} dB</span>
                      </div>
                    )}
                    <div className="pt-1 border-t flex justify-between font-bold">
                      <span>Pérdida Total:</span>
                      <span className="text-red-600">-{integratedBudget.perdidaTotal.toFixed(2)} dB</span>
                    </div>
                  </div>

                  {integratedBudget.advertencias.length > 0 && (
                    <div className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                      {integratedBudget.advertencias.map((adv, i) => (
                        <p key={i}>⚠️ {adv}</p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {!integratedBudget && (
              <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 rounded p-2">
                No se pudo calcular el presupuesto óptico detallado (falta información de nodos/cables en la ruta).
                El conteo de la API (conectores/empalmes) puede diferir del modelo de pérdidas con splitters.
              </p>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleIlluminateTrace}
                className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors flex items-center justify-center gap-2"
              >
                <Zap className="w-4 h-4" />
                Iluminar Ruta
              </button>
              <button
                onClick={handleExportPDF}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Exportar PDF
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <AlertCircle className="w-16 h-16 mb-3 text-red-500" />
            <p className="text-sm font-medium text-red-600 dark:text-red-400">
              No se pudo trazar la fibra
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {activeTrace.error || 'Error desconocido'}
            </p>
          </div>
        )}
      </div>
    </aside>
  )
}

// Componente auxiliar para segmento de ruta
function PathSegmentItem({
  segment,
  index,
  isLast,
}: {
  segment: PathSegment
  index: number
  isLast: boolean
}) {
  const nodes = useNodeStore((s) => s.nodes)
  const cables = useFiberStore((s) => s.cables)
  const node = nodes.find((n) => n.id === segment.nodeId)
  const cable = segment.cableId ? cables.find((c) => c.id === segment.cableId) : undefined

  const refCode = segment.nodeRefCode || (node as { refCode?: string })?.refCode
  const nombre = segment.description || (node as { nombre?: string })?.nombre
  const tipo = node?.tipo || segment.nodeType
  const titleParts = [refCode, nombre].filter(Boolean)
  const title = titleParts.length > 0 ? titleParts.join(' · ') : segment.nodeId
  const subtitleTipo = tipo && !title.toLowerCase().includes(String(tipo).toLowerCase()) ? tipo : null

  let cableLabel: string | null = null
  if (segment.cableId) {
    if (cable) {
      cableLabel = cableLabelShort(cable as Cable)
    } else {
      const bits = [segment.cableCodigo && `[${segment.cableCodigo}]`, segment.cableNombre].filter(
        Boolean
      ) as string[]
      cableLabel = bits.length > 0 ? bits.join(' ') : `Cable …${segment.cableId.slice(0, 8)}…`
    }
  }

  return (
    <div className="relative">
      <div className="flex items-start gap-2">
        <div className="flex flex-col items-center">
          <div className="w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">
            {index + 1}
          </div>
          {!isLast && <div className="w-0.5 h-8 bg-gray-300 dark:bg-dark-600 my-1" />}
        </div>

        <div className="flex-1 pb-2">
          <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-2">
            <p className="font-medium text-sm leading-snug">{title}</p>
            {subtitleTipo && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {subtitleTipo}
              </p>
            )}
            {cableLabel && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                <span className="text-gray-500">Cable:</span> {cableLabel}
              </p>
            )}
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-xs text-gray-500">{segment.distanceKm.toFixed(2)} km</span>
              {segment.hasSplice && (
                <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded">
                  Empalme
                </span>
              )}
              {segment.spliceVerificado === false && (
                <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 px-2 py-0.5 rounded">
                  ⚠️ Sin verificar
                </span>
              )}
              {segment.spliceVerificado === true && (
                <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                  ✓ Verificado
                </span>
              )}
            </div>
            <details className="mt-1.5">
              <summary className="text-[10px] text-gray-400 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300">
                IDs técnicos
              </summary>
              <p className="text-[10px] font-mono break-all text-gray-500 mt-0.5">
                Nodo: {segment.nodeId}
                {segment.cableId ? (
                  <>
                    <br />
                    Cable: {segment.cableId}
                  </>
                ) : null}
              </p>
            </details>
          </div>
        </div>
      </div>
    </div>
  )
}

function SplicePointRow({ nodeId }: { nodeId: string }) {
  const node = useNodeStore((s) => s.nodes.find((n) => n.id === nodeId))
  const ref = (node as { refCode?: string })?.refCode
  const nom = (node as { nombre?: string })?.nombre
  const label = [ref, nom].filter(Boolean).join(' · ') || nodeId

  return (
    <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded p-2 text-xs">
      <div className="flex justify-between items-start gap-2">
        <div>
          <span className="font-medium leading-snug">{label}</span>
          {node && (
            <span className="block text-[10px] text-orange-800/80 dark:text-orange-200/80 uppercase">
              {node.tipo}
            </span>
          )}
        </div>
        <span className="text-orange-600 dark:text-orange-400 shrink-0">~0.1 dB</span>
      </div>
      {label !== nodeId && (
        <p className="text-[10px] font-mono text-gray-500 dark:text-gray-400 mt-1 break-all">{nodeId}</p>
      )}
    </div>
  )
}

